/**
 * Image Selection Logic (NOT URL construction)
 *
 * This utility ONLY handles selecting which image field to use from data objects.
 * It delegates URL construction to the url utils.
 *
 * @see {@link src/utils/urlUtils.ts} - For URL construction and path handling
 * @see {@link src/components/ui/DefaultImage.tsx} - Component using this logic
 * @see {@link src/components/research/components/runningImageString/RunningImageHandler.tsx} - Usage example
 */

import { defaultImages, getFullImageUrl } from "@/utils/urlUtils";

/**
 * Interface defining the image data that can be passed to imgSelector
 */
interface ImageData {
  // Company image fields
  fileLogoSquare?: string;
  fileLogoRectangle?: string;
  fileLogo?: string;

  // Person image fields
  profileImage?: string; // Current field name for person images

  // Legacy fields (keeping for backward compatibility)
  personPicture?: string; // Legacy field name for person images

  // Generic image field
  picture?: string;

  // Allow for other properties with safer unknown type
  [key: string]: unknown;
}

/**
 * Parameters for the imgSelector function
 */
interface ImgSelectorParams {
  /** Object containing image data fields */
  data: ImageData;
  /** Type of image: "person" or "company" (defaults to "company" if not provided) */
  imgType?: "person" | "company" | string;
}

/**
 * Determines the correct image URL based on available image fields
 * Falls back to default images if no image is available.
 *
 * This function:
 * 1. Selects the first available image field from the data object
 * 2. Applies proper URL formatting including extension handling
 * 3. Returns a fully-qualified URL with the appropriate base path
 *
 * @returns A valid image URL or path to a default image
 */
export const imgSelector = ({
  data,
  imgType = "company",
}: ImgSelectorParams): string => {
  // Get the appropriate default image based on type using the centralized utility
  const defaultImage = defaultImages.getByType(imgType);

  // Early return if no data is provided
  if (!data) {
    return defaultImage;
  }

  const {
    fileLogoSquare,
    fileLogoRectangle,
    fileLogo,
    profileImage, // Current field name for person images
    personPicture, // Legacy field name for person images
    picture,
  } = data;

  // For person images, prioritize current field names over legacy ones
  const personImage = profileImage || personPicture;

  // Select the right image based on type
  let selectedImage: string | undefined;

  if (imgType === "person") {
    // For person images, use person-specific fields first
    selectedImage = personImage || picture;
  } else {
    // For company and other images, use company-specific fields first
    selectedImage = fileLogoSquare || fileLogoRectangle || fileLogo || picture;
  }

  // If no image is found, return the default image immediately
  if (!selectedImage) {
    return defaultImage;
  }

  // Process the logo path - add jpg extension if missing
  const imageWithExt = selectedImage?.split(".")?.[1]
    ? selectedImage
    : [selectedImage, "jpg"]?.join(".");

  // Delegate URL construction to urlUtils.ts
  // This ensures all URL construction logic is centralized
  const imageUrl = getFullImageUrl(imageWithExt, defaultImage);

  // Debug in development mode only
  if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
    console.debug(
      `[imgSelector] Selected image field "${selectedImage}" for type "${imgType}", final URL: ${imageUrl}`
    );
  }

  return imageUrl;
};
